import { ProjectsTypes } from "../../../interfaces/ProjectsTypes";
import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { db } from "../../../api/firebase";
import {
  collection,
  addDoc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import moment from "moment-timezone";
import Input from "../../../components/Input";
import { colors } from "../../../assets/colors";
import Row from "../../../components/layout/Row";
import DialogTask from "../components/DialogTask";
import { Task } from "../../../interfaces/TaskTypes";
import { createHistory } from "../../../api/history";
import { ActionTypeEnum } from "../../../interfaces/HistoryTypes";
import { mountHistory } from "../../../utils/history";
import { USER_DATA } from "../../../utils/constants";

interface Props {
  currentProject: ProjectsTypes;
}

export default function KanbanTab({ currentProject }: Props) {
  const [tasks, setTasks] = useState<any>([]);
  const [openInput, setOpenInput] = useState<string>("");

  const [openTask, setOpenTask] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "tasks"), (snapshot) => {
      const loadedTasks = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTasks(loadedTasks);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const tasksQuery = query(
      collection(db, "tasks"),
      where("project_id", "==", currentProject.project_id) // Filtro pelo ID do projeto
    );

    const unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
      const loadedTasks = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTasks(loadedTasks);
    });

    return () => unsubscribe();
  }, [currentProject.project_id]); // Dependência para atualizar caso o projeto mude

  const initialColumns: { [key: string]: Task[] } = {
    Todo: [],
    "Pronto pra fazer": [],
    "Em desenvolvimento": [],
    "Pronto para testes": [],
    "Em testes": [],
    Finalizado: [],
  };

  const [newTaskTitle, setNewTaskTitle] = useState("");

  const addTask = async () => {
    if (newTaskTitle.trim() === "") return;

    const historyEntry = {
      date: moment().tz("America/Sao_Paulo").format(), // Data e hora
      previousStatus: null, // Status anterior
      newStatus: openInput, // Novo status
      type: "criou",
      user: JSON.parse(localStorage.getItem(USER_DATA) || "{}").name,
    };

    const newTask = {
      id: String(new Date().getTime()), // gere um ID único, ou use um gerador de IDs
      title: newTaskTitle,
      status: openInput, // status inicial
      description: "",
      created_at: moment().tz("America/Sao_Paulo").format(),
      project_id: currentProject.project_id,
      owner: JSON.parse(localStorage.getItem(USER_DATA) || "{}").name,
      history: [historyEntry],
    };
    await addDoc(collection(db, "tasks"), newTask); // Adicione o novo cartão ao Firestore

    setNewTaskTitle(""); // Limpa o campo após a adição
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    // Criar uma cópia profunda do estado
    const updatedTasks = tasks.map((task: Task) => ({ ...task }));

    // Encontrar a tarefa movida
    const movedTaskIndex = updatedTasks.findIndex(
      (task: Task) => task.id === result.draggableId
    );

    if (movedTaskIndex === -1) return;

    const movedTask = { ...updatedTasks[movedTaskIndex] };
    movedTask.status = result.destination.droppableId;

    // Atualizar a lista de tarefas no estado
    updatedTasks.splice(movedTaskIndex, 1);
    updatedTasks.push(movedTask); // Reinsere a task no array

    setTasks(updatedTasks); // Atualiza o estado com a nova posição da tarefa

    const historyEntry = {
      date: moment().tz("America/Sao_Paulo").format(), // Data e hora
      previousStatus: result.source.droppableId, // Status anterior
      newStatus: result.destination.droppableId, // Novo status
      type: "status",
      user: JSON.parse(localStorage.getItem(USER_DATA) || "{}").name,
    };

    //   // Atualiza o Firestore
    const taskDocRef = query(
      collection(db, "tasks"),
      where("id", "==", movedTask.id)
    );
    const querySnapshot = await getDocs(taskDocRef);

    if (!querySnapshot.empty) {
      const docRef = querySnapshot.docs[0].ref;
      await updateDoc(docRef, {
        status: movedTask.status,
        history: [
          ...(querySnapshot.docs[0].data().history || []),
          historyEntry,
        ], // Adiciona histórico
      })
        .then(() => {
          console.log("Tarefa atualizada com sucesso");
        })
        .catch((error) => {
          console.error("Erro ao atualizar a tarefa: ", error);
        });
    }
  };

  const columns: { [key: string]: Task[] } = tasks.reduce(
    (acc: Record<string, Task[]>, task: Task) => {
      if (acc[task.status]) {
        acc[task.status].push(task); // Adiciona a tarefa na coluna correta
      } else {
        console.warn("Status não reconhecido:", task.status); // Para status não reconhecido
      }
      return acc; // Retorna o acumulador para o próximo loop
    },
    initialColumns
  );

  const openAddTaskInput = (columnName: string) => {
    setOpenInput(columnName);
    setNewTaskTitle("");
  };

  const cancelAddTask = () => {
    setOpenInput("");
    setNewTaskTitle("");
  };

  const handleAddTask = () => {
    setNewTaskTitle("");
    addTask().then(() => {
      createHistory(
        mountHistory(
          currentProject.project_id || "",
          ActionTypeEnum.CREATE,
          "Tarefa: " + newTaskTitle
        )
      );
    });
  };

  const handleOpenTask = (task: Task) => () => {
    setOpenTask(true);
    setSelectedTask(task);
  };

  return (
    <Box sx={styles.container}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box display="flex" justifyContent="space-between" gap={2}>
          {Object.entries(columns).map(([columnName, columnTasks]) => (
            <Droppable key={columnName} droppableId={String(columnName)}>
              {(provided: any) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={styles.column}
                >
                  <Row space style={styles.collumnHeader}>
                    <span style={styles.collumnName}>{columnName}</span>

                    <i
                      className="material-icons"
                      style={styles.openAddTaskButton}
                      onClick={() => openAddTaskInput(columnName)}
                    >
                      add
                    </i>
                  </Row>

                  {columnTasks.map((task: Task, index: number) => (
                    <Draggable
                      key={task.id}
                      draggableId={task.id}
                      index={index}
                    >
                      {(provided: any) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          onClick={handleOpenTask(task)}
                          style={{
                            ...provided.draggableProps.style,
                            ...styles.card,
                          }}
                        >
                          <span style={{ fontSize: 16 }}>{task.title}</span>

                          <Row space>
                            <span style={{ fontSize: 12, color: colors.gray }}>
                              {moment(task.created_at)
                                .tz("America/Sao_Paulo")
                                .format("DD MMM HH:mm")}
                            </span>
                            <span style={{ fontSize: 12, color: colors.gray }}>
                              {task.owner}
                            </span>
                          </Row>
                        </Box>
                      )}
                    </Draggable>
                  ))}

                  {columnName === openInput && (
                    <Box sx={styles.addTaskContent}>
                      <Input
                        variant="outlined"
                        onBlur={() => {
                          newTaskTitle == "" && setOpenInput("");
                        }}
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)} // Atualiza o título
                        onKeyPress={(e) => e.key === "Enter" && addTask()} // Adiciona ao pressionar Enter
                        style={styles.addTaskInput}
                        multiline
                        rows={2}
                      />
                      <Row style={styles.saveAddTaskContent}>
                        <i
                          className="material-icons"
                          style={styles.cancelAddTaskButton}
                          onClick={cancelAddTask}
                        >
                          close
                        </i>
                        <i
                          className="material-icons"
                          style={styles.addTaskButton}
                          onClick={handleAddTask}
                        >
                          check
                        </i>
                      </Row>
                    </Box>
                  )}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          ))}
        </Box>
      </DragDropContext>

      {openTask && (
        <DialogTask
          close={() => setOpenTask(false)}
          open={openTask}
          task={selectedTask}
        />
      )}
    </Box>
  );
}
const styles: any = {
  container: {
    display: "flex",
    flexDirection: "column",
    padding: 4,
    maxHeight: "calc(100vh - 100px)",
    minWidth: "1200px",
  },

  column: {
    padding: 16,
    width: 350,
    minHeight: 600,
    backgroundColor: colors.background,
    borderRadius: 8,
  },

  collumnHeader: {
    display: "flex",
    alignItems: "center",
  },

  collumnName: {
    fontSize: 12,
    color: "#10957d",
    backgroundColor: "rgba(16, 149, 125, 0.15)",
    padding: 2,
    paddingLeft: 6,
    paddingRight: 6,
    borderRadius: 6,
  },

  openAddTaskButton: {
    color: "#10957d",
    backgroundColor: "rgba(16, 149, 125, 0.15)",
    padding: 2,
    borderRadius: 100,
    fontSize: 16,
    cursor: "pointer",
  },

  card: {
    margin: "16px 0",
    padding: "16px",
    borderRadius: 8,
    backgroundColor: colors.white,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  addTaskButton: {
    color: "#10957d",
    backgroundColor: "rgba(16, 149, 125, 0.15)",
    padding: 4,
    borderRadius: 100,
    fontSize: 16,
    cursor: "pointer",
  },

  cancelAddTaskButton: {
    color: colors.gray,
    backgroundColor: colors.background,
    padding: 4,
    borderRadius: 100,
    fontSize: 16,
    cursor: "pointer",
  },

  saveAddTaskContent: {
    justifyContent: "flex-end",
    gap: 6,
    marginTop: 8,
  },

  addTaskInput: {
    flex: 1,
    display: "flex",
    backgroundColor: colors.white,
  },

  addTaskContent: {
    flexDirection: "column",
    display: "flex",
    alignItems: "flex-end",
    marginTop: 0,
  },
};
